<script setup>
import StartupPlate from "@/components/trackers/StartupPlate.vue";
import {inject, provide, ref, watch} from "vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import StartupCardPopup from "@/components/trackers/StartupCardPopup"
import Reviews from "@/components/trackers/Reviews.vue";
import AnswersView from "@/components/trackers/AnswersView.vue";
import InviteStartup from "@/components/startups/InviteStartup.vue";
import ReviewEdit from "@/components/trackers/ReviewEdit.vue";
import Board from "@/views/trackers/tracker/Board.vue";
import {trackerTypes} from "@/constants/trackers";

const emit = defineEmits(['reviewUpdated']);

const tracker = inject('tracker').value;
const addCellData = inject("addCellData");
const trackerUpdated = inject("trackerUpdated");
const serverFilters = inject("serverFilters");
const companiesListUpdated = inject("companiesListUpdated");
const filteredCompaniesCount = inject("filteredCompaniesCount");
const filtersApplied = inject("filtersApplied");
const removeCompany = inject("removeCompany");
const addStartupsToStage = inject("addStartupsToStage");

const board = ref(null);
const startupCardPopup = ref(null);
const editReviewModal = ref(null);
const reviewsModal = ref(null);
const viewAnswersModal = ref(null);
const inviteModal = ref(null);
const reopenDetails = ref(null);
const currentStartup = ref(null);

watch(serverFilters, () => {
    filteredCompaniesCount.value = null;
    board.value.reset();
});

watch(companiesListUpdated, (value) => {
    if (value) {
        board.value.reset();
    }
});

watch(trackerUpdated, (value) => {
    if (value) {
        board.value.reset();
    }
});

function getStagesData() {
    return trackersCompaniesService.getStagesData(tracker.id, {filters: serverFilters.value})
}

function getStageCompanies(stageId, stageLastLinkId = 0) {
    return trackersCompaniesService.getStageCompanies(tracker.id, stageId, stageLastLinkId, {filters: serverFilters.value})
}

function saveFunnelChanges(changedItems) {
    return trackersCompaniesService.savePipelineCompanies(tracker.id, {stagesCompanies: changedItems});
}

async function getStartupData() {
    const {data} = await trackersCompaniesService.getStartupData(tracker.id, currentStartup.value.id);

    for (let field in data) {
        currentStartup.value[field] = data[field];
    }
}

function editReview(startup, fromModal = false) {
    currentStartup.value = startup;
    editReviewModal.value.open(startup.id)

    if (fromModal) {
        reopenDetails.value = startup.id;
        startupCardPopup.value.close();
    }
}

function editReviewFromModal(startupId) {
    editReviewModal.value.open(startupId)
    reopenDetails.value = startupId;
    startupCardPopup.value.close();
}

function reviewsUpdated() {
    startupCardPopup.value.getData();
    emit('reviewUpdated');

    if (!('ratingFilterState' in serverFilters.value) || serverFilters.value.ratingFilterState === 'all') {
        getStartupData();
    } else {
        board.value.reset();
    }
}

function editReviewClosed() {
    if (reopenDetails.value) {
        startupCardPopup.value.open(reopenDetails.value);
        reopenDetails.value = null;
    }
}

function showReviews(startupId) {
    reviewsModal.value.open(tracker.id, startupId);
}

function showAnswers(startupId) {
    viewAnswersModal.value.open(tracker.id, startupId)
}

function showDetails(startup) {
    currentStartup.value = startup;
    startupCardPopup.value.open(startup.id)
}

function inviteStartup(startupId) {
    inviteModal.value.open(startupId);
}

function countChanged(count) {
    if (filtersApplied.value) {
        filteredCompaniesCount.value = count;
    }
}

async function togglePinned(startup) {
    startup.pinned = !startup.pinned;

    await trackersCompaniesService.setPinned(tracker.id, startup.id, startup.pinned);

    board.value.rearrangePinned(startup.id);
}

provide('getStagesData', getStagesData);
provide('getStageItems', getStageCompanies);
provide('saveFunnelChanges', saveFunnelChanges);
</script>

<template>
    <div>
        <Board
            ref="board"
            @countChanged="countChanged"
        >
            <template #item="{item: startup}">
                <StartupPlate
                    :startup="startup"
                    :draggable="startup.draggable"
                    :addData="addCellData"
                    :hasReviews="tracker.reviews_enabled === 1"
                    @editReview="editReview(startup)"
                    @showReviews="showReviews(startup.id)"
                    @showAnswers="showAnswers(startup.id)"
                    @cardClicked="showDetails(startup)"
                    @invite="inviteStartup"
                    @togglePinned="togglePinned(startup)"
                />
            </template>

            <template #addLink="{stageId}">
                <a
                    v-if="tracker.type !== trackerTypes.PUBLIC"
                    class="button-oblong"
                    @click="addStartupsToStage(stageId)"
                >
                    + Add Startup
                </a>
            </template>
        </Board>

        <StartupCardPopup
            ref="startupCardPopup"
            :tracker-id="tracker.id"
            :tracker-type="tracker.type"
            :permissions="tracker.user_permissions"
            @editReview="(startupId) => editReviewFromModal(startupId)"
            @showReviews="showReviews"
            @removeStartup="removeCompany"
        />

        <Reviews
            ref="reviewsModal"
            :tracker-id="tracker.id"
            :has-overall-score="tracker.has_overall_score"
            @reviewSaved="reviewsUpdated"
        />

        <ReviewEdit
            ref="editReviewModal"
            :tracker-id="tracker.id"
            @reviewSaved="reviewsUpdated"
            @closed="editReviewClosed"
        />

        <AnswersView
            ref="viewAnswersModal"
        />

        <InviteStartup
            ref="inviteModal"
            :tracker-id="tracker.id"
        />
    </div>
</template>

<style scoped>

</style>
